import React from "react"

// configs
import config from "config"

// firestore
import { Firestore } from "services/firebase"
import { getDoc, doc, updateDoc } from "firebase/firestore"
import { orderBy } from "lodash-es"
import { fixTimes, fixZusatz } from "utils/menu"

const MenuContext = React.createContext()

const parseSmartString = (str) => {
  const regex = /^([A-Za-z]+)(\d+)([A-Za-z]*)$/
  const match = str.match(regex)
  if (!match) return null
  const [, prefix, numStr, suffix] = match
  return {
    prefix: prefix.toLowerCase(),
    number: parseInt(numStr, 10),
    suffix: suffix.toLowerCase(),
  }
}

const sortList = (arr) =>
  orderBy(
    arr,
    [
      // 1) numbers first if id is a number (just in case)
      (item) => (typeof item.id === "number" ? 0 : 1),

      // 2) numeric order if id is a number
      (item) => (typeof item.id === "number" ? item.id : Infinity),

      // 3) prefix inside id
      (item) => {
        if (typeof item.id !== "string") return "~"
        const parsed = parseSmartString(item.id)
        return parsed ? parsed.prefix : "~"
      },

      // 4) number inside id
      (item) => {
        if (typeof item.id !== "string") return Infinity
        const parsed = parseSmartString(item.id)
        return parsed ? parsed.number : Infinity
      },

      // 5) suffix inside id → M2 < M2b < M3
      (item) => {
        if (typeof item.id !== "string") return "~"
        const parsed = parseSmartString(item.id)
        return parsed ? parsed.suffix : ""
      },
    ],
    ["asc", "asc", "asc", "asc", "asc"]
  )

function useMenu() {
  const context = React.useContext(MenuContext)
  if (!context) {
    throw new Error(`useMenu must be used within a MenuProvider`)
  }

  const [menu, setMenu] = context.menu
  const [status] = context.status
  const [attributes, setAttributes] = context.attributes

  const updateMenu = async (newMenu) => {
    const menuRef = doc(Firestore, "menus", config.key)
    await updateDoc(menuRef, {
      data: newMenu,
      updated_at: new Date(),
    })

    setMenu(newMenu)
    setAttributes((prev) => ({
      ...prev,
      updated_at: new Date(),
    }))
  }

  const updatePrices = async (newPrices) => {
    const newMenu = menu.map((cat) => ({
      ...cat,
      items: cat.items.map((item) => ({
        ...item,
        new_price: newPrices[item.id] || item.new_price || item.preis,
      })),
    }))

    // console.log("Update menu prices:")
    // console.log("new prices: ", newPrices)
    // console.log("newMenu: ", newMenu)

    const menuRef = doc(Firestore, "menus", config.key)
    await updateDoc(menuRef, {
      data: newMenu,
      updated_at: new Date(),
    })

    setMenu(newMenu)
    setAttributes((prev) => ({
      ...prev,
      updated_at: new Date(),
    }))
  }

  const publishPrices = async () => {
    const newMenu = menu.map((cat) => ({
      ...cat,
      items: cat.items.map((item) => ({
        ...item,
        preis: item.new_price || item.preis,
      })),
    }))

    const menuRef = doc(Firestore, "menus", config.key)
    await updateDoc(menuRef, {
      data: newMenu,
      updated_at: new Date(),
    })

    setMenu(newMenu)
    setAttributes((prev) => ({
      ...prev,
      updated_at: new Date(),
    }))
  }

  return {
    menu: !menu
      ? null
      : menu
          .filter((cat) =>
            //    key: "chicken",
            // label: "Gà - Chicken",
            // icon: "chicken",
            // zusatz: [4],
            // info: "Hühnerbrustfilet mit frischem Gemüse und Reis als Beilage.",
            // items: [
            ["key", "label", "icon", "items"].every((key) =>
              Object.keys(cat).includes(key)
            )
          )
          .map((cat) => ({
            ...cat,
            items: sortList(cat.items).filter((item) =>
              //    id: 40,
              // name: "Hühnerbrustfilet: Sojasauce",
              // preis: 13.0,
              // info: "Mit Sojasauce",
              // scharf: 0,
              // vegetarisch: false,
              // zusatz: ["f"],
              ["id", "name", "preis"].every((key) =>
                Object.keys(item).includes(key)
              )
            ),
          })),
    status,
    attributes,
    updateMenu,
    updatePrices,
    publishPrices,
  }
}

function MenuProvider(props) {
  const [menuValue, setMenu] = React.useState()
  const [attributesValue, setAttributes] = React.useState()
  const [statusValue, setStatus] = React.useState("idle")

  React.useEffect(() => {
    const loadMenu = async () => {
      setStatus("loading")
      const data = await getDoc(doc(Firestore, "menus", config.key))

      if (!data.exists()) {
        console.error("❌ Menu does not exist")
        setStatus("error")
        return
      }

      const menudata = data.data().data.map((cat) => ({
        ...cat,
        zusatz: fixZusatz(cat.zusatz),
        times: fixTimes(cat.times),

        items: cat.items.map((item) => ({
          ...item,
          zusatz: fixZusatz(item.zusatz),
          times: fixTimes(item.times),
        })),
      }))

      console.log("✅ Loaded menu")

      if (process.env.NEXT_PUBLIC__APP_ENV === "development") {
        console.log("Menu data:", menudata)
      }

      setMenu(menudata)

      const { created_at, updated_at } = data.data()
      setAttributes({
        created_at,
        updated_at,
      })
      setStatus("ready")
    }
    loadMenu()
  }, [])

  const menu = React.useMemo(() => [menuValue, setMenu], [menuValue])
  const attributes = React.useMemo(
    () => [attributesValue, setAttributes],
    [attributesValue]
  )
  const status = React.useMemo(() => [statusValue, setStatus], [statusValue])

  return (
    <MenuContext.Provider value={{ menu, attributes, status }} {...props} />
  )
}

export { MenuProvider, useMenu }
